import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Form, Button } from 'react-bootstrap5';
import { FileUploader } from 'react-drag-drop-files';
import { useField } from 'formik';

import Alert from 'components/new_ui/Alerts/alert';
import IconWithText from 'components/Widgets/IconWithText';

import CropImage from 'components/new_ui/Images/CropImage';

export default function HorizontalFileField(props) {
  const {
    name,
    label,
    fileTypes,
    showPreview,
    hint,
    disabled,
    useCrop,
  } = props;

  const [field,, helpers] = useField(props);
  const { setValue } = helpers;
  const [previewUrl, setPreviewUrl] = useState(field.value);
  const [onDrag, setOnDrag] = useState(false);

  const [showCropImage, setShowCropImage] = useState(false);

  const onChange = (file) => {
    setValue(file);

    if (showPreview) {
      setPreviewUrl(URL.createObjectURL(file));
      if (useCrop) {
        setShowCropImage(true);
      }
    }
  };

  const onDraggingStateChange = (dragging) => {
    setOnDrag(dragging);
  };

  const removeFile = (e) => {
    e.stopPropagation();
    setValue(null);
    setPreviewUrl(null);
    setShowCropImage(false);
  };

  const onTypeError = (_error) => {
    Alert({
      title: 'Unsupported file type',
      text: `Only files with the following extensions are allowed: ${fileTypes.join(', ')}.`,
      confirmButtonText: 'Close',
    });
  };

  const onDragElement = useMemo(() => (
    <div className="my-4 fw-semibold">
      <IconWithText
        iconClassName="upload-icon bg-gray-700 mx-auto"
        wrapperClassName="mb-2"
      />
      Drop your file to upload
    </div>
  ), []);

  const onSelectFileElement = useMemo(() => (
    <div className="my-4">
      <div className="fs-5 text-gray-700 fw-semibold">
        Drag your file here
      </div>

      <div className="d-flex align-items-center my-2">
        <div className="flex-fill ms-5 me-3">
          <hr className="border rounded-pill opacity-100" />
        </div>

        <div className="text-gray-700 fw-semibold">
          or
        </div>

        <div className="flex-fill ms-3 me-5">
          <hr className="border rounded-pill opacity-100" />
        </div>
      </div>

      <Button>Browse Files</Button>

      <div className={`mt-3 text-gray-${field.value ? 700 : 300} fw-light`}>
        {field.value ? (
          <>
            {field.value.name}
            <br />
          </>
        ) : (
          <>
            Supported file formats &ndash;
            {' '}
            {fileTypes.join(', ')}
          </>
        )}
      </div>
    </div>
  ), [field.value]);

  const imagePreview = useMemo(() => (
    <>
      <img
        src={previewUrl}
        alt="file upload preview"
        className="mw-100 mh-100"
      />

      <Button
        onClickCapture={removeFile}
        variant="link"
        className="icon-with-text position-absolute top-0 end-0 p-2"
      >
        <i className="trash-icon small-icon bg-gray-600" />
      </Button>
    </>
  ), [previewUrl]);

  const workArea = onDrag ? onDragElement : onSelectFileElement;
  const content = previewUrl === null ? workArea : imagePreview;

  return (
    <Form.Group className="pb-3" controlId="formFile">
      {label && (
        <Form.Label>
          <>
            {label}
          </>
        </Form.Label>
      )}

      {!showCropImage && (
      <div style={{ height: '276px' }}>
        <FileUploader
          name={name}
          types={fileTypes}
          hoverTitle=" "
          onDraggingStateChange={onDraggingStateChange}
          handleChange={onChange}
          onTypeError={onTypeError}
          classes={
            `w-100 h-100 p-4 border rounded-4 text-center cursor-pointer outline-none ${disabled ? 'opacity-50' : ''}`
          }
          dropMessageStyle={{ backgroundColor: '#FFFFFF', border: 'none' }}
          disabled={disabled}
        >
          {content}
        </FileUploader>
      </div>
      )}
      {showCropImage
          && (
          <div className="w-100 h-100 p-4 border rounded-4   outline-none" style={{ position: 'relative' }}>
            <CropImage imgSrc={previewUrl} setValue={setValue} />
            <Button
              onClickCapture={removeFile}
              variant="link"
              className="icon-with-text position-absolute top-0 end-0 p-2"
            >
              <i className="trash-icon small-icon bg-gray-600" />
            </Button>
          </div>
          )}

      {hint && <Form.Text className="text-muted">{hint}</Form.Text>}
    </Form.Group>
  );
}

HorizontalFileField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  fileTypes: PropTypes.arrayOf(PropTypes.string),
  showPreview: PropTypes.bool,
  hint: PropTypes.string,
  disabled: PropTypes.bool,
  useCrop: PropTypes.bool,
};

HorizontalFileField.defaultProps = {
  label: '',
  fileTypes: ['JPG', 'PNG', 'GIF'],
  showPreview: true,
  hint: '',
  disabled: false,
  useCrop: false,
};
